import { useEffect, useState } from "react";
import { http, formatTRY, catByValue } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Wallet, CircleCheck, Clock, Sparkles } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";
import { Link } from "react-router-dom";

function MetricCard({ icon: Icon, label, value, sub, tone = "emerald", testId }) {
  const tones = {
    emerald: "from-emerald-500 to-emerald-700",
    amber: "from-amber-500 to-amber-700",
    sky: "from-sky-500 to-sky-700",
    indigo: "from-indigo-500 to-indigo-700",
    red: "from-red-500 to-red-700",
  };
  return (
    <Card className="p-6 border-slate-200 hover:shadow-md transition-shadow" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</div>
          <div className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Outfit" }}>{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${tones[tone]} flex items-center justify-center text-white shrink-0`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    http.get("/stats/summary").then(({ data }) => setStats(data));
  }, []);

  if (!stats) return <div className="text-slate-500">Yükleniyor...</div>;

  const catData = [
    { name: "Ev", value: stats.category_distribution.ev, color: "#10B981" },
    { name: "İnşaat", value: stats.category_distribution.insaat, color: "#F59E0B" },
    { name: "Ofis", value: stats.category_distribution.ofis, color: "#0284C7" },
    { name: "Yönetim", value: stats.category_distribution.yonetim, color: "#4F46E5" },
  ].filter(x => x.value > 0);

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={TrendingUp} label="Bu Ay Gelir" value={formatTRY(stats.total_income)}
                    sub={`${stats.total_jobs_this_month} iş`} tone="emerald" testId="total-revenue-card" />
        <MetricCard icon={TrendingDown} label="Bu Ay Gider" value={formatTRY(stats.total_expense)}
                    tone="red" testId="total-expense-card" />
        <MetricCard icon={Wallet} label="Net Kâr" value={formatTRY(stats.net_profit)}
                    sub="Bu ay" tone="indigo" testId="net-profit-card" />
        <MetricCard icon={Clock} label="Bekleyen Tahsilat" value={formatTRY(stats.pending_collections)}
                    tone="amber" testId="pending-collections-card" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 border-slate-200" data-testid="monthly-chart-card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Gelir & Gider (6 Ay)</h3>
              <p className="text-xs text-slate-500 mt-1">Son 6 ayın karşılaştırması</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stats.monthly_chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `₺${v/1000}k`} />
              <Tooltip formatter={(v) => formatTRY(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
              <Legend />
              <Bar dataKey="gelir" fill="#10B981" name="Gelir" radius={[6, 6, 0, 0]} />
              <Bar dataKey="gider" fill="#EF4444" name="Gider" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 border-slate-200" data-testid="category-chart-card">
          <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}>İş Kategorileri</h3>
          {catData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={catData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={45}>
                  {catData.map((e) => <Cell key={e.name} fill={e.color} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-sm text-slate-400 py-16">Henüz iş yok</div>
          )}
          <div className="space-y-2 mt-4">
            {catData.map((c) => (
              <div key={c.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />
                  <span className="text-slate-700">{c.name}</span>
                </div>
                <span className="font-semibold text-slate-900">{c.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-6 border-slate-200" data-testid="todays-jobs-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Bugünün İşleri</h3>
            <p className="text-xs text-slate-500 mt-1">{stats.todays_jobs_count} planlanmış iş</p>
          </div>
          <Link to="/isler" className="text-sm text-emerald-700 hover:underline font-medium">Tümünü Gör →</Link>
        </div>
        {stats.todays_jobs.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Sparkles className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            Bugün için planlanmış iş yok
          </div>
        ) : (
          <div className="space-y-2">
            {stats.todays_jobs.map((j) => {
              const c = catByValue(j.category);
              return (
                <div key={j.id} className="flex items-center gap-4 p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
                  <div className="h-10 w-1 rounded-full" style={{ background: c.hex }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{j.title}</div>
                    <div className="text-xs text-slate-500 truncate">{j.address || "-"} · {j.customer_name || "-"}</div>
                  </div>
                  <Badge variant="outline" className="hidden sm:inline-flex">{c.label}</Badge>
                  <div className="text-right">
                    <div className="font-bold text-slate-900">{formatTRY(j.price)}</div>
                    <div className="text-xs text-slate-500">{j.employee_name || "Atanmadı"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
